import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Wallet, Target, ListChecks, AlertTriangle, Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import { CompanyDialog, TeamManager, OwnerAccount } from "@/components/MasterForms";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useStore } from "@/store/AppStore";
import { PageHeader, Panel, KpiCard } from "@/components/kit";
import { LinesChart, GOLD, INK } from "@/components/Charts";
import { RankingTable, ProcessList } from "@/components/Lists";
import { ChecklistManager } from "@/components/ChecklistForms";
import { TodayTable, MonthReport, AbsenceManager } from "@/components/Attendance";
import { TimesheetPicker } from "@/components/Timesheet";
import { PROFILES } from "@/data/mock";
import { TaskBoard } from "@/components/TaskBoard";
import { MaintenanceManager } from "@/components/Maintenance";
import { SealBadge } from "@/components/Seal";
import { MonthHighlights, YearHighlights } from "@/components/Highlights";
import { companySummary, companyHistory, ranking, nameMap } from "@/lib/selectors";
import { brl } from "@/lib/format";

const TABS = [["resumo", "Resumo"], ["equipe", "Equipe"], ["processos", "Processos"], ["tarefas", "Tarefas"], ["checklists", "Checklists"], ["manutencao", "Manutenção"], ["presenca", "Presença"]];

export default function MasterCompanyDetail() {
  const { id } = useParams();
  const { state } = useStore();
  const company = state.companies.find((c) => c.id === id);
  if (!company) return <p data-testid="company-not-found">Empresa não encontrada.</p>;
  const r = companySummary(state, company);
  const tasks = state.tasks.filter((t) => t.companyId === id);

  return (
    <div data-testid="master-company-detail-page">
      <Link to="/master/empresas" data-testid="back-to-companies" className="mb-6 inline-flex items-center gap-2 text-xs font-semibold text-neutral-500 hover:text-gold-dark"><ArrowLeft className="h-3.5 w-3.5" /> Empresas</Link>
      <PageHeader eyebrow={company.segment} title={company.name} subtitle={<>{`${company.city} · Empresária ${company.owner}`} <span className="ml-1 align-middle"><SealBadge level={state.seals[id]?.level} testId="detail-seal" /></span></>} actions={<CompanyDialog company={company} testId="edit-company" trigger={<Button variant="outline" data-testid="edit-company-button"><Pencil className="mr-1.5 h-4 w-4 text-gold-dark" /> Editar empresa</Button>} />} />
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard testId="detail-kpi-sales" icon={Wallet} label="Vendas do mês" value={brl(r.sales)} hint={`Meta ${brl(company.salesGoal)}`} />
        <KpiCard testId="detail-kpi-goal" icon={Target} label="Meta atingida" value={`${r.att}%`} />
        <KpiCard testId="detail-kpi-tasks" icon={ListChecks} label="Tarefas concluídas" value={`${r.taskRate}%`} hint={`${tasks.length} tarefas cadastradas`} />
        <KpiCard testId="detail-kpi-overdue" icon={AlertTriangle} label="Atrasadas" value={r.overdue} hint="Tarefas fora do prazo" />
      </div>
      <Tabs defaultValue="resumo" className="mt-8">
        <TabsList className="h-auto flex-wrap justify-start bg-neutral-100 p-1">
          {TABS.map(([v, l]) => <TabsTrigger key={v} value={v} data-testid={`detail-tab-${v}`} className="data-[state=active]:bg-white">{l}</TabsTrigger>)}
        </TabsList>
        <TabsContent value="resumo" className="mt-6">
          <Panel title="Evolução de vendas" subtitle="Últimos 6 meses x meta atual">
            <LinesChart data={companyHistory(state, company)} xKey="mes" series={[{ key: "vendas", name: "Vendas", color: INK }, { key: "meta", name: "Meta", color: GOLD, dashed: true }]} />
          </Panel>
        </TabsContent>
        <TabsContent value="equipe" className="mt-6 space-y-6">
          <Panel title="Acesso da empresária" subtitle="Crie o login da empresária desta empresa"><OwnerAccount company={company} /></Panel>
          <Panel title="Gerenciar equipe" subtitle="Adicione, edite ou remova colaboradoras"><TeamManager companyId={id} /></Panel>
          <Panel title="Ranking da equipe"><RankingTable rows={ranking(state, id)} /></Panel>
          <MonthHighlights companyId={id} />
          <YearHighlights companyId={id} />
        </TabsContent>
        <TabsContent value="processos" className="mt-6"><Panel title="Processos mapeados"><ProcessList processes={state.processes.filter((p) => p.companyId === id)} /></Panel></TabsContent>
        <TabsContent value="tarefas" className="mt-6"><TaskBoard tasks={tasks} mode="owner" companyId={id} actor={PROFILES.master.name} title="Tarefas da equipe" /></TabsContent>
        <TabsContent value="presenca" className="mt-6 space-y-6">
          <TodayTable companyId={id} />
          <MonthReport companyId={id} />
          <TimesheetPicker companyId={id} />
          <AbsenceManager companyId={id} />
        </TabsContent>
        <TabsContent value="manutencao" className="mt-6"><MaintenanceManager companyId={id} actor={PROFILES.master.name} /></TabsContent>
        <TabsContent value="checklists" className="mt-6">
          <ChecklistManager companyId={id} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
